import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
// import axios from 'axios';
import { QRCodeCanvas } from 'qrcode.react';
import ArticlesCarousel from '../components/ArticlesCarousel';
import FaqAccordion from '../components/FaqAccordion';
import TipsList from '../components/TipsList';
import {
  Image, FolderArchive,
  Copy, Scissors, RotateCw, Repeat, Zap, ImagePlus, Camera, Maximize2,
  Rocket, Lock, Smartphone, Sparkles, Wand2, ScanText,
  Link2, Wifi, MessageCircle, FileText, QrCode, ArrowRight,
  FileCheck, Layers, Check
} from 'lucide-react';

const toolGroups = [
  {
    title: 'PDF Tools',
    subtitle: 'All-in-One PDF converter',
    tools: [
      // PDF-to-Word, Word-to-PDF, PDF-to-Excel, and Excel-to-PDF are
      // temporarily removed from navigation — the LibreOffice binary these
      // rely on isn't available on the current host, so the conversion
      // fails server-side. Re-add once the office-conversion microservice
      // is live (see office-service/). Direct URL hits are safely
      // redirected home by ConvertPage.js in the meantime.
      // PDF to JPG (needs poppler on the server) and Compress PDF (doesn't
      // shrink files yet — always reports 0% saved) are hidden until they
      // actually work, so AdSense reviewers don't hit a broken tool.
      // { icon: Image, color: '#eff6ff', iconColor: '#3b82f6', label: 'PDF to JPG', desc: 'Export PDF pages as images', href: '/convert/pdf-to-jpg' },
      { icon: FolderArchive, color: '#eff6ff', iconColor: '#3b82f6', label: 'JPG to PDF', desc: 'Create PDF from images', href: '/convert/jpg-to-pdf' },
      { icon: Copy, color: '#fdf4ff', iconColor: '#a855f7', label: 'Merge PDF', desc: 'Combine multiple PDFs into one', href: '/merge-pdf' },
      { icon: Scissors, color: '#fdf4ff', iconColor: '#a855f7', label: 'Split PDF', desc: 'Split PDF into separate files', href: '/split-pdf' },
      // { icon: Shrink, color: '#fff7ed', iconColor: '#f97316', label: 'Compress PDF', desc: 'Reduce PDF file size', href: '/compress-pdf' },
      { icon: RotateCw, color: '#fff7ed', iconColor: '#f97316', label: 'Rotate PDF', desc: 'Rotate pages in your PDF', href: '/rotate-pdf' },
    ]
  },
  {
    title: 'Image Tools',
    subtitle: 'Convert and optimize images in any format',
    tools: [
      { icon: Repeat, color: '#f0fff4', iconColor: '#22c55e', label: 'JPG to PNG', desc: 'Convert JPG to transparent PNG', href: '/image-convert?from=jpg&to=png' },
      { icon: Repeat, color: '#eff6ff', iconColor: '#3b82f6', label: 'PNG to JPG', desc: 'Convert PNG to compressed JPG', href: '/image-convert?from=png&to=jpg' },
      { icon: Zap, color: '#fdf4ff', iconColor: '#a855f7', label: 'Image to WebP', desc: 'Compress images with WebP format', href: '/image-convert?to=webp' },
      { icon: ImagePlus, color: '#fff7ed', iconColor: '#f97316', label: 'Any Image Format', desc: 'Convert between JPG, PNG, WebP, GIF, BMP, TIFF...', href: '/image-convert' },
      { icon: Camera, color: '#E8F1FF', iconColor: '#005CFC', label: 'Image to PDF', desc: 'Pack images into a PDF file', href: '/image-convert?to=pdf' },
      { icon: Maximize2, color: '#f0fff4', iconColor: '#22c55e', label: 'Resize Image', desc: 'Resize images to any dimension', href: '/image-convert?mode=resize' },
      { icon: Wand2, color: '#fdf4ff', iconColor: '#a855f7', label: 'Remove Background', desc: 'AI background removal, free', href: '/remove-background' },
      { icon: ScanText, color: '#eff6ff', iconColor: '#3b82f6', label: 'Image to Text (OCR)', desc: 'Extract editable text from images', href: '/image-to-text' },
    ]
  }
];

export default function Home() {
  // Only fed the hidden "Files Converted" stat below.
  // const [stats, setStats] = useState({ totalConversions: 0 });
  const [showAllTips, setShowAllTips] = useState(false);

  // useEffect(() => {
  //   axios.get('/api/stats').then(r => setStats(r.data)).catch(() => {});
  // }, []);

  return (
    <div>
      
       <Helmet>
        <title>ILoveConvert — Free Online File Converter | PDF, Word, Images</title>
        <meta name="description" content="Free online file converter. Merge, split and rotate PDFs, convert JPG to PDF, JPG to PNG, remove image backgrounds and extract text from images. No signup needed." />
        <meta name="keywords" content="pdf converter, free file converter, merge pdf, split pdf, rotate pdf, jpg to pdf, jpg to png, image converter, qr code generator, remove background, image to text" />
        <link rel="canonical" href="https://iloveconvert.in/" />
        <meta property="og:title" content="ILoveConvert — Free Online File Converter" />
        <meta property="og:description" content="Free PDF and image tools — merge, split, rotate, convert images, remove backgrounds. Fast, no signup." />
        <meta property="og:url" content="https://iloveconvert.in/" />
        <meta property="og:type" content="website" />
        <meta name="twitter:title" content="ILoveConvert — Free Online File Converter" />
        <meta name="twitter:description" content="Free PDF and image converter. Merge, split, rotate, convert images — no signup needed." />
      </Helmet>

      {/* HERO */}
      <section className="hero">
        <h1>Convert Any File Online — Fast, Free & Easy <span>PDF, JPG, PNG, WebP, QR code generator & More</span></h1>
        <p>Merge, split and rotate PDFs, convert images, remove backgrounds and create QR codes in just a few clicks — no software installation required.</p>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Link to="/merge-pdf" className="btn btn-primary btn-lg hero-cta-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}><Rocket size={18} /> Start Converting Free</Link>
          <Link to="/image-convert" className="btn btn-outline btn-lg" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}><Image size={18} /> Browse All Tools</Link>
        </div>
        <div style={{ display: 'flex', gap: '10px 24px', justifyContent: 'center', flexWrap: 'wrap', marginTop: 22, marginBottom: 6 }}>
          {[
            'Your files stay private',
            'Easy to convert',
            'Works on all devices',
            'Safe and secure',
          ].map(item => (
            <div key={item} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-muted)' }}>
              <Check size={15} style={{ color: '#22c55e', flexShrink: 0 }} /> {item}
            </div>
          ))}
        </div>
        <div className="hero-stats">
          {/* "Files Converted" showed a hard-coded "50+ K" until the real
              count passed 50,000 — the real count is far lower, so it's
              hidden until it can show the true number.
          <div className="hero-stat">
            <div className="num">{stats.totalConversions > 50000 ? stats.totalConversions.toLocaleString() : '50+ K'}</div>
            <div className="label">Files Converted</div>
          </div>
          */}
          <div className="hero-stat">
            <div className="num">9</div>
            <div className="label">Image Formats</div>
          </div>
          <div className="hero-stat">
            <div className="num">100%</div>
            <div className="label">Free Forever</div>
          </div>
          <div className="hero-stat">
            <div className="num" style={{ display: 'flex', justifyContent: 'center' }}><Lock size={28} /></div>
            <div className="label">Secure & Private</div>
          </div>
        </div>
           <div className="container" style={{ maxWidth: 900, margin: '40px auto' }}>
  <p style={{ fontSize: '15px', color: 'var(--text-muted)', lineHeight: 1.7 }}>
    ILoveConvert is a free online file conversion platform that lets you merge, split and rotate
    PDFs, turn images into PDFs, and convert images between formats with just a few clicks. Fast,
    free, secure, and easy to use on any device. No signup. No downloads. Just upload,
    convert, and go!
  </p>
</div>
      </section>

      {/* TOOL GROUPS — placed right after the hero, before any marketing
          copy below, since converting a file (not reading about why to
          trust the site) is what almost every visitor actually came to do. */}
      {toolGroups.map((group) => (
        <section className="section" key={group.title} style={{ background: group.title === 'Image Tools' ? '#f8fafc' : 'white' }}>
          <div className="container">
            <h2 className="section-title">{group.title}</h2>
            <p className="section-sub">{group.subtitle}</p>
            <div className="tools-grid">
              {group.tools.map((tool) => (
                <Link to={tool.href} className="tool-card" key={tool.label}>
                  <div className="tool-icon" style={{ background: tool.color, color: tool.iconColor }}>
                    <tool.icon size={26} strokeWidth={1.75} />
                  </div>
                  <h3>{tool.label}</h3>
                  <p>{tool.desc}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      ))}

      {/* QR CODE GENERATOR PROMO */}
      <section className="section" style={{ background: 'linear-gradient(135deg, #1D3557, #005CFC 55%, #193A6D)' }}>
        <div className="container">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 40, alignItems: 'center' }}>
            <div>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.25)', borderRadius: 999, padding: '6px 14px', marginBottom: 18, color: 'white', fontSize: '0.8rem', fontWeight: 700 }}>
                <QrCode size={15} /> ALSO FREE — BONUS TOOL
              </div>
              <h2 style={{ color: 'white', fontFamily: 'Sora, sans-serif', fontSize: 'clamp(1.6rem,4vw,2.4rem)', marginBottom: 14, lineHeight: 1.15 }}>
                Free QR Code Generator
              </h2>
              <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: '1rem', lineHeight: 1.7, marginBottom: 24, maxWidth: 480 }}>
                A QR code is a quick way to share almost anything. Add a link, Wi-Fi details, UPI ID,
                contact, WhatsApp, location, or whatever you need, and your QR code is ready in seconds.
                You can also give it your own look with custom colours, a logo, or a background.
                No signup, no limits — just create and use it for free.
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 28 }}>
                {[
                  { icon: Link2, label: 'URL' },
                  { icon: Wifi, label: 'WiFi' },
                  { icon: MessageCircle, label: 'WhatsApp' },
                  { icon: FileText, label: 'vCard' },
                ].map(b => (
                  <div key={b.label} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.25)', borderRadius: 10, padding: '7px 12px', color: 'white', fontSize: '0.82rem', fontWeight: 600 }}>
                    <b.icon size={15} /> {b.label}
                  </div>
                ))}
              </div>
              <Link to="/qr-code-generator" className="btn btn-lg" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: 'white', color: '#005CFC', fontWeight: 700 }}>
                Create Your QR Code <ArrowRight size={18} />
              </Link>
            </div>

            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <Link
                to="/qr-code-generator"
                className="qr-promo-card"
                style={{
                  display: 'block', background: 'white', borderRadius: 24, padding: 24, textDecoration: 'none',
                  boxShadow: '0 20px 55px rgba(0,92,252,0.35), 0 8px 22px rgba(0,0,0,0.18)'
                }}
              >
                <QRCodeCanvas value="https://iloveconvert.in/qr-code-generator" size={220} fgColor="#0B1B33" bgColor="#ffffff" level="H" />
                <p style={{ textAlign: 'center', marginTop: 12, fontSize: '0.78rem', color: '#64748b', fontWeight: 600 }}>Scan or click to try it yourself</p>
              </Link>
            </div>
          </div>
        </div>
      </section>

<section className="hero">
      {/* ── SEO Content Block — restructured from 4 bare paragraphs into
          icon cards. Same substantive text, condensed and given visual
          structure so it doesn't read as an SEO text-dump between two
          card-based sections. ── */}
<div style={{ maxWidth: 900, margin: '48px auto 0', padding: '0 20px 60px', fontFamily: 'DM Sans, sans-serif' }}>
  <h2 style={{ textAlign: 'center' }}>Free Online File Converter for All Needs</h2>
  <p style={{ textAlign: 'center', color: 'var(--text-muted)', maxWidth: 640, margin: '0 auto 32px', fontSize: '0.95rem', lineHeight: 1.7 }}>
    Work with PDFs and images in just a few clicks. No installation, no technical skills, and no complicated steps — just upload your file, choose the format, and convert.
  </p>
  <div className="home-auto-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
    {[
      { icon: Zap, title: 'Fast & Easy', desc: 'Upload, convert, and download your file in seconds. Works on phone, tablet, or computer — no installation needed.' },
      { icon: FileCheck, title: 'Good Quality', desc: 'Your file keeps its quality, format, and layout after conversion.' },
      { icon: Lock, title: 'Safe & Private', desc: 'Your files are securely processed and automatically deleted. We never store or share them.' },
      { icon: Layers, title: 'Everything in One Place', desc: 'Convert PDFs, images, documents, and more with simple online tools — anytime, anywhere.' },
    ].map(f => (
      <div key={f.title} style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 12, padding: '18px 16px' }}>
        <div style={{ marginBottom: 10, color: '#005CFC' }}><f.icon size={24} strokeWidth={1.75} /></div>
        <div style={{ fontFamily: 'Sora, sans-serif', fontSize: 14, fontWeight: 700, color: 'var(--text)', marginBottom: 6 }}>{f.title}</div>
        <div style={{ fontSize: 14, color: 'var(--text-muted)', lineHeight: 1.6 }}>{f.desc}</div>
      </div>
    ))}
  </div>
  </div>
</section>

      {/* FEATURES SECTION — deliberately placed between the two light
          sections above and below (rather than stacked against the QR
          promo section, which is also blue) so no two adjacent sections
          share the same background. */}
      <section className="section" style={{ background: 'linear-gradient(135deg, #1D3557, #005CFC 55%, #193A6D)' }}>
        <div className="container text-center">
          <h2 className="section-title" style={{ color: 'white' }}>Why Use ILoveConvert?</h2>
          <p className="section-sub" style={{ color: 'rgba(255,255,255,0.7)' }}>
           Everything you need in one place to convert files quickly and securely</p>
          <div className="home-auto-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 24, marginTop: 32 }}>
            {[
              { icon: Zap, title: 'Lightning Fast', desc: 'Files converted in seconds, not minutes' },
              { icon: Lock, title: 'Privacy Focused', desc: 'HTTPS uploads. Files deleted automatically within 2 hours' },
              { icon: Smartphone, title: 'Mobile Friendly', desc: 'Works perfectly on any device' },
              { icon: Sparkles, title: 'No Signup Needed', desc: 'Start converting instantly, no account required' },
            ].map(f => (
              <div key={f.title} style={{ background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)', borderRadius: 16, padding: '28px 20px', color: 'white', border: '1px solid rgba(255,255,255,0.15)' }}>
                <div style={{ marginBottom: 12 }}><f.icon size={32} strokeWidth={1.75} /></div>
                <h3 style={{ fontFamily: 'Sora, sans-serif', marginBottom: 8 }}>{f.title}</h3>
                <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.88rem' }}>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

<section className="hero">
  <div style={{ maxWidth: 900, margin: '48px auto 0', padding: '0 20px 60px', fontFamily: 'DM Sans, sans-serif' }}>
  <hr style={{ border: 'none', borderTop: '1px solid var(--border)', margin: '0 0 40px' }} />

  {/* ── How it works ──────────────────────────────────────── */}
 <h1>How it works <span>Instantly</span></h1>
 <h2 style={{ fontFamily: 'Sora, sans-serif', fontSize: 18, fontWeight: 700, color: 'var(--text)', marginBottom: 16, textAlign: 'left' }}>Convert any file in 4 simple steps</h2>
  <TipsList tips={[
      { label: 'Upload your file', tip: 'Drag and drop, or click to browse. Supports PDFs and images up to 50 MB — no account needed.' },
      { label: 'Choose your output format', tip: 'We detect your file type and show only the formats it can actually convert to, so there\'s nothing to guess.' },
      { label: 'Click Convert', tip: 'Processing runs on our servers — no software or plugins to install. Most files finish in seconds.' },
      { label: 'Download your file', tip: 'Get your converted file instantly. Both the original and the result are auto-deleted from our servers within 2 hours.' },
    ]} />
  </div>
</section>

 <section className="section" style={{ background: '#EEF4FF' }}>
  <div  style={{
  maxWidth: 900, margin: '38px auto 0', padding: '0 20px 60px',
  fontFamily: 'DM Sans, sans-serif'
}}>
      <h2 className="section-title">All Supported Formats</h2>
<div className="home-auto-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px,1fr))', gap: 12, marginBottom: 40 }}>
    {[
      // Office conversions (PDF ⇄ Word/Excel) and PDF → JPG are switched
      // off, so they're not listed here until they work again.
      // { title: 'PDF documents', tags: ['PDF → DOCX','PDF → XLSX','PDF → JPG','DOCX → PDF','XLSX → PDF','JPG → PDF'], desc: 'Convert PDF to Word, Excel, or JPG. You can also convert Word, Excel, and images to PDF.' },
      { title: 'PDF documents', tags: ['JPG → PDF','PNG → PDF','Merge PDF','Split PDF','Rotate PDF'], desc: 'Turn images into a PDF, combine several PDFs into one, split a PDF into pages, or fix page orientation.' },
      { title: 'Images',        tags: ['JPG → PNG','PNG → JPG','JPG → WebP','PNG → WebP','More formats available'], desc: 'Convert your images between JPG, PNG, WebP, GIF, BMP, AVIF, and ICO. Simple, quick, and easy to use.' },
    ].map(card => (
      <div key={card.title} style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 12, padding: '16px 18px' }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', marginBottom: 10 }}>{card.title}</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
          {card.tags.map(t => (
            <span key={t} style={{ fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 20, border: '1px solid var(--border)', background: '#f8fafc', color: 'var(--text-muted)' }}>{t}</span>
          ))}
        </div>
        <div style={{ fontSize: 14, color: 'var(--text-muted)', lineHeight: 1.6 }}>{card.desc}</div>
      </div>
    ))}
  </div>
</div>
</section>
 <section className="section" style={{ background: '#f8fafc' }}>
       
<div  style={{
  maxWidth: 900, margin: '38px auto 0', padding: '0 20px 60px',
  fontFamily: 'DM Sans, sans-serif'
}}>
    <h2 className="section-title">Why People Choose Our Converter</h2>
  <div className="home-auto-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px,1fr))', gap: 12, marginBottom: 40 }}>
    {[
      { color: '#3B82F6', title: 'No Downloads',            desc: 'Use it straight from your browser. No software or installation needed.' },
      { color: '#22C55E', title: 'Your Files Stay Private', desc: 'Files are transferred securely and deleted automatically. We don’t share your files.' },
      { color: '#F59E0B', title: 'Good Quality',            desc: 'Your converted files keep their quality, layout, and formatting as much as possible.' },
      { color: '#A855F7', title: 'Free to Use',             desc: 'Convert files up to 50 MB with no watermarks and no paid plans.' },
      { color: '#005CFC', title: 'Quick Conversions',       desc: 'Most files are ready in just a few seconds. No long waiting.' },
      { color: '#0EA5E9', title: 'Works on Any Device',     desc: 'Use it on your phone, tablet, or computer. Upload and download your files wherever you are.' },
    ].map(card => (
      <div key={card.title} style={{ background: 'white', border: '1px solid var(--border)', borderLeft: `3px solid ${card.color}`, borderRadius: '0 12px 12px 0', padding: '14px 18px' }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', marginBottom: 6 }}>{card.title}</div>
        <div style={{ fontSize: 14, color: 'var(--text-muted)', lineHeight: 1.6 }}>{card.desc}</div>
      </div>
    ))}
  </div>
</div>
</section>
 <section className="section hero" style={{ background: 'white' }}>
       
 <div  style={{
  maxWidth: 900, margin: '38px auto 0', padding: '0 20px 60px',
  fontFamily: 'DM Sans, sans-serif'
}}>
  {/* ── Pro tips — compact grid, short one-line tips (full explanations
      already live on each tool's own page). Collapsed to 6 by default on
      screen so it doesn't dominate the homepage's length; "Show all"
      expands in place — no extra page/route needed. ── */}

    <h1 style={{ textAlign: 'center' }}>Tips for Better <span> Results</span></h1>
    <p className="section-sub" style={{ marginBottom: 28 }}>Simple tips to help you get the best results from every tool.</p>
    <div className="home-auto-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px,1fr))', gap: 12 }}>
      {[
        { color: '#3B82F6', label: 'Merge in Order', tip: 'Arrange your PDFs in the right order before merging — it saves reordering pages later.' },
        { color: '#22C55E', label: 'Make Images Smaller', tip: 'Use WebP to reduce image size while keeping good quality.' },
        { color: '#F59E0B', label: 'Images to PDF', tip: 'Combine multiple images into one PDF when you need to share them together.' },
        { color: '#A855F7', label: 'Split Large PDFs', tip: 'Split a long PDF and share only the pages people actually need.' },
        { color: '#005CFC', label: 'Keep Your Formatting', tip: 'Clean and well-organized files usually give better conversion results.' },
        { color: '#0EA5E9', label: 'Scanned Documents', tip: 'Scanned PDFs may need OCR to turn the text into editable content.' },
        { color: '#EC4899', label: 'Pick the Right Format', tip: 'Use PDF for sharing, DOCX for editing, and JPG/PNG for images.' },
        { color: '#14B8A6', label: 'For Websites', tip: 'Smaller images can help your website load faster.' },
        { color: '#F97316', label: 'Check File Size', tip: 'Keep your file under 50 MB for smoother processing.' },
        { color: '#6366F1', label: 'Use Good-Quality Files', tip: 'A clear, good-quality original usually gives a better result.' },
        { color: '#84CC16', label: 'Convert Files Carefully', tip: 'When converting many files, doing them one at a time can help avoid mistakes.' },
        { color: '#EF4444', label: 'Keep Your Files Safe', tip: 'Choose a converter that deletes your files after processing.' },
        { color: '#06B6D4', label: 'Check Compatibility', tip: 'Choose a format that works with the device or software you plan to use.' },
        { color: '#8B5CF6', label: 'Check Before Sharing', tip: 'Open your converted file once to make sure everything looks right.' },
        { color: '#10B981', label: 'Use PDF for Final Files', tip: 'PDF helps keep your document layout the same across different devices.' },
      ].slice(0, showAllTips ? 15 : 6).map(t => (
        <div key={t.label} style={{ background: '#f8fafc', border: '1px solid var(--border)', borderLeft: `3px solid ${t.color}`, borderRadius: '0 12px 12px 0', padding: '16px 18px', textAlign: 'left' }}>
          <div style={{ fontSize: 14, fontWeight: 700, fontFamily: 'Sora, sans-serif', color: 'var(--text)', marginBottom: 6 }}>{t.label}</div>
          <div style={{ fontSize: 14, color: 'var(--text-muted)', lineHeight: 1.6 }}>{t.tip}</div>
        </div>
      ))}
    </div>
    <div style={{ textAlign: 'center', marginTop: 20 }}>
      <button onClick={() => setShowAllTips(v => !v)} className="btn btn-outline">
        {showAllTips ? 'Show fewer tips' : 'Show all 15 tips'}
      </button>
    </div>
</div>
</section>
<div  style={{
  maxWidth: 900, margin: '48px auto 0', padding: '0 20px 60px',
  fontFamily: 'DM Sans, sans-serif', alignItems: 'center',
}}>
  {/* ── FAQ ───────────────────────────────────────────────── */}
     <h2 className="section-title">Frequently Asked Questions</h2>
   <FaqAccordion faqs={[
      { q: 'Is the file converter free?',
        a: 'Yes. You can convert your files for free, without signing up or paying for a plan.' },
      { q: 'Are my files private?',
        a: 'Yes. Your files are handled securely and are automatically deleted after processing.' },
      { q: 'What is the maximum file size?',
        a: 'You can upload files up to 50 MB.' },
      { q: 'Do I need an account?',
        a: 'No. Just upload your file and start converting. No signup is needed.' },
      // PDF → Word is switched off, so this FAQ is hidden for now.
      // { q: 'Why does my Word file look different from the PDF?',
      //   a: 'Some PDFs use special fonts, images, or layouts that may not convert exactly the same way. Simple, text-based PDFs usually give better results.' },
      { q: 'Can I convert files on my phone?',
        a: 'Yes. The converter works on iPhone, Android, tablets, and computers.' },
    ]} />
</div>

<ArticlesCarousel />
</div>


  );
}
